import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useLeague } from '../contexts/LeagueContext';
import { useUser } from '../contexts/UserContext';
import { useAuth } from '../contexts/AuthContext';
import { Timestamp } from 'firebase/firestore';
import type { CourseModel } from '../models/CourseModels';
import LoadingSpinner, { ButtonSpinner } from '../components/LoadingSpinner';
import { dayIdToDateString } from '@/utils/toolkit';
import usePageTitle from '@/hooks/usePageTitle';
import { encodeKey } from '@/utils/toolkit';
import { RoundService } from '@/services/RoundService';
import { RoundModel } from '@/models/RoundModels';
import { resolveType } from '@/services/ServiceContainer';
import { CourseService } from '@/services/CourseService';
import { UserDashboardService } from '@/services/UserDashboardService';

const StartLeagueRound: React.FC = () => {
    const { theme } = useTheme();
    const { user } = useAuth();
    const navigate = useNavigate();
    const { leagueId, courseId, dayId } = useParams<{ leagueId: string; courseId: string; dayId: string }>();
    const { leagues } = useLeague();
    const { appUser } = useUser();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const entryMode = searchParams.get('entryMode') || 'holeByHole';
    const [courseModel, setCourseModel] = useState<CourseModel | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedHoles, setSelectedHoles] = useState<'front9' | 'back9' | 'all18'>('front9');
    const [startingRound, setStartingRound] = useState(false);

    const formatDayId = (dayId: string) => {
        const date = new Date(dayIdToDateString(dayId));
        return `${date.getMonth() + 1}/${date.getDate()}`;
    };

    // Set page title
    usePageTitle('Start League Round');

    const league = leagues[leagueId || ''];
    // Find the scheduleItem from league.schedule using dayId
    const scheduleItem = Array.isArray(league?.schedule)
        ? league.schedule.find((item: any) => item.dayId === dayId)
        : undefined;

    // Find the current user in league.members (by uid, then encodedEmail)
    let member = league?.members[appUser?.uid || ''] || null;
    if (member === null) {
        member = league?.members[appUser?.encodedEmail || ''];
    }
    // Map the flightId from the member object to the appropriate entry in league.flights
    let flight = null;
    if (member && member.flightId && Array.isArray(league?.flights)) {
        flight = league.flights.find((f: any) => f.id === member.flightId);
    }

    // Check for active round and redirect if found
    useEffect(() => {
        const checkForActiveRound = async () => {
            if (!leagueId || !dayId || !appUser) return;

            try {

                const roundService = resolveType(RoundService);
                const userDashboardService = resolveType(UserDashboardService);
                const userDashboard = await userDashboardService.get(appUser.uid);
                if (userDashboard) {
                    const currentRoundId = userDashboard.activeRoundId;
                    if (currentRoundId) {
                        const currentRound = await roundService.get(currentRoundId);
                        if (currentRound) {
                            if (currentRound.setup?.type === 'league' &&
                                currentRound.setup.league?.id === leagueId &&
                                currentRound.setup.league?.dayId === dayId &&
                                currentRound.status === 'active') {
                                console.log('Found active round. Should never get here. Redirecting to dashboard');
                                navigate('/dashboard', { replace: true });
                            }
                        }
                    }
                }
            } catch (err) {
                console.error('Error checking current round:', err);
                setError('Failed to check current round');
                setLoading(false);
            };
        }

        checkForActiveRound();
    }, [leagueId, dayId, appUser, navigate]);

    useEffect(() => {
        const loadCourse = async () => {
            if (!leagueId || !courseId) {
                setError('Missing required information');
                setLoading(false);
                return;
            }

            try {
                const courseService = resolveType(CourseService);
                const course = await courseService.get(courseId);
                if (course != null) {
                    setCourseModel(course);
                    // Set initial holesToPlay based on schedule item
                    setSelectedHoles(scheduleItem?.holesToPlay || 'front9');
                } else {
                    setError('Course not found');
                }

            } catch (err) {
                console.error('Error loading course:', err);
                setError('Failed to load course information');
            } finally {
                setLoading(false);
            }
        };

        loadCourse();
    }, [leagueId, courseId, scheduleItem, league]);

    if (loading) {
        return <LoadingSpinner />;
    }

    if (error || !league || !courseModel) {
        return (
            <div style={{ color: theme.colors.ERROR, padding: theme.spacing.MEDIUM }}>
                {error || 'League or course not found'}
            </div>
        );
    }

    const handleStartRound = async () => {
        if (!user || !courseModel) return;

        setStartingRound(true);
        setError(null);

        try {
            // Create a new round document

            const now = Timestamp.now();
            // Determine the correct tee for the user from their flight
            let selectedTeeName = courseModel.scorecard.holes[0].tees[0].name;
            if (flight && flight.teeName) {
                selectedTeeName = flight.teeName;
            }
            // Create the round data
            const roundModel: RoundModel = {
                id: '',  // set by RoundService.create()
                creatorId: user.uid,
                setup: {
                    courseId: courseModel.id,
                    courseName: courseModel.name,
                    flightId: member?.flightId ?? null,
                    selectedTee: selectedTeeName,
                    startingHole: selectedHoles === 'back9' ? 10 : 1,
                    holesToPlay: selectedHoles,
                    league: {
                        id: leagueId,
                        dayId: dayId
                    },
                    type: 'league' as const
                },
                users: {
                    [user.uid]: {
                        uid: user.uid,
                        encodedEmail: encodeKey(user.email || '', 'user_'),
                        displayName: user.displayName || '',
                        selectedTee: selectedTeeName,
                        handicap: member?.handicap ?? null,
                        flightId: member?.flightId ?? null,
                        holeStats: [],
                        skinsAmount: 0
                    }
                },
                status: 'active' as const,
                createdAt: now,
                startedAt: now,
                updatedAt: now,
                completedAt: null,
                lockedAt: null
            };

            const roundService = resolveType(RoundService);
            const roundId = await roundService.create(roundModel, user.uid, courseModel);

            // Navigate based on entry mode
            if (entryMode === 'scoreSummary') {
                navigate(`/scores-only-entry/${courseId}/${roundId}`);
            } else {
                const startingHole = selectedHoles === 'back9' ? 10 : 1;
                navigate(`/hole-entry/${courseId}/${roundId}/${startingHole}`);
            }
        } catch (error) {
            console.error('Error starting round:', error);
            setError('Failed to start round');
            setStartingRound(false);
        }
    };

    return (
        <div style={{ padding: theme.spacing.MEDIUM }}>
            <h1 style={{
                color: theme.colors.TEXT,
                marginBottom: theme.spacing.LARGE,
                fontSize: theme.fonts.LARGE
            }}>
                {league.name}
            </h1>

            <div style={{ marginBottom: theme.spacing.LARGE }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: theme.spacing.MEDIUM,
                }}>
                    <h2 style={{
                        color: theme.colors.TEXT,
                        fontSize: theme.fonts.LARGE,
                        margin: 0,
                    }}>
                        {courseModel.name}
                    </h2>

                    <h2 style={{
                        color: theme.colors.TEXT,
                        fontSize: theme.fonts.LARGE,
                        margin: 0,
                    }}>
                        {formatDayId(dayId || '')}
                    </h2>
                </div>

                {/* Display the flight and handicap on the same row */}
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: theme.spacing.MEDIUM,
                    }}
                >
                    <span style={{
                        color: theme.colors.TEXT,
                        fontWeight: 600,
                        fontSize: theme.fonts.MEDIUM,
                        background: theme.colors.SURFACE,
                        borderRadius: theme.borderRadius.SMALL,
                        padding: '5px',
                        margin: '5px',
                        border: `1px solid ${theme.colors.BORDER}`,
                    }}>
                        Flight: {flight?.name || 'N/A'}
                    </span>
                    <span style={{
                        color: theme.colors.TEXT,
                        fontWeight: 600,
                        fontSize: theme.fonts.MEDIUM,
                        background: theme.colors.SURFACE,
                        borderRadius: theme.borderRadius.SMALL,
                        padding: '5px',
                        margin: '5px',
                        border: `1px solid ${theme.colors.BORDER}`,
                    }}>
                        Hcp: {member?.handicap}
                    </span>
                </div>

                {/* Selected Holes Text */}
                <div style={{
                    textAlign: 'center',
                    color: theme.colors.TEXT_SECONDARY,
                    marginBottom: theme.spacing.SMALL,
                    fontSize: theme.fonts.MEDIUM,
                }}>
                    ({scheduleItem?.holesToPlay === 'front9' ? 'Front 9' : 'Back 9'})
                </div>

                {/* Toggle Button Group for Front 9 and Back 9 */}
                <div
                    style={{
                        display: 'flex',
                        gap: 0,
                        marginBottom: theme.spacing.LARGE,
                        borderRadius: theme.borderRadius.MEDIUM,
                        overflow: 'hidden',
                        boxShadow: `0 1px 4px rgba(0,0,0,0.06)`,
                        border: `1px solid ${theme.colors.BORDER}`,
                        width: '100%',
                        maxWidth: 400,
                        marginLeft: 'auto',
                        marginRight: 'auto',
                    }}
                >
                    <button
                        onClick={() => setSelectedHoles('front9')}
                        style={{
                            flex: 1,
                            padding: theme.spacing.MEDIUM,
                            backgroundColor: selectedHoles === 'front9' ? theme.colors.PRIMARY : theme.colors.SURFACE,
                            color: selectedHoles === 'front9' ? theme.colors.BACKGROUND : theme.colors.TEXT,
                            border: 'none',
                            borderRight: `1px solid ${theme.colors.BORDER}`,
                            borderRadius: `${theme.borderRadius.MEDIUM} 0 0 ${theme.borderRadius.MEDIUM}`,
                            fontWeight: 'bold',
                            fontSize: theme.fonts.MEDIUM,
                            cursor: 'pointer',
                            outline: 'none',
                            transition: 'background 0.2s, color 0.2s',
                        }}
                    >
                        Front 9
                    </button>
                    <button
                        onClick={() => setSelectedHoles('back9')}
                        style={{
                            flex: 1,
                            padding: theme.spacing.MEDIUM,
                            backgroundColor: selectedHoles === 'back9' ? theme.colors.PRIMARY : theme.colors.SURFACE,
                            color: selectedHoles === 'back9' ? theme.colors.BACKGROUND : theme.colors.TEXT,
                            border: 'none',
                            borderLeft: `1px solid ${theme.colors.BORDER}`,
                            borderRadius: `0 ${theme.borderRadius.MEDIUM} ${theme.borderRadius.MEDIUM} 0`,
                            fontWeight: 'bold',
                            fontSize: theme.fonts.MEDIUM,
                            cursor: 'pointer',
                            outline: 'none',
                            transition: 'background 0.2s, color 0.2s',
                        }}
                    >
                        Back 9
                    </button>
                </div>

                <button
                    onClick={handleStartRound}
                    style={{
                        padding: theme.spacing.MEDIUM,
                        backgroundColor: theme.colors.PRIMARY,
                        color: theme.colors.BACKGROUND,
                        border: 'none',
                        borderRadius: theme.borderRadius.MEDIUM,
                        cursor: startingRound ? 'default' : 'pointer',
                        opacity: startingRound ? 0.7 : 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: theme.spacing.SMALL,
                        width: '100%',
                        fontSize: theme.fonts.LARGE,
                        fontWeight: 'bold'
                    }}
                    disabled={startingRound}
                >
                    {startingRound ? (
                        <ButtonSpinner size={20} color={theme.colors.BACKGROUND} />
                    ) : 'Start Round'}
                </button>

                {/* Pin Prizes Section */}
                {scheduleItem?.pinPrizes && scheduleItem.pinPrizes.length > 0 && (
                    <div style={{
                        marginTop: theme.spacing.LARGE,
                        background: theme.colors.SURFACE,
                        borderRadius: theme.borderRadius.MEDIUM,
                        padding: theme.spacing.MEDIUM,
                        border: `1px solid ${theme.colors.BORDER}`,
                    }}>
                        <h3 style={{
                            color: theme.colors.TEXT,
                            fontWeight: 'bold',
                            marginBottom: theme.spacing.MEDIUM,
                            fontSize: theme.fonts.MEDIUM
                        }}>
                            Pin Prizes
                        </h3>
                        <ul style={{ paddingLeft: 0, margin: 0, listStyle: 'none' }}>
                            {scheduleItem.pinPrizes.map((prize: any, idx: number) => (
                                <li key={idx} style={{
                                    marginBottom: theme.spacing.SMALL,
                                    color: theme.colors.TEXT
                                }}>
                                    <div style={{ fontWeight: 600 }}>
                                        Hole {prize.hole}
                                        {prize.teeName ? ` (${prize.teeName})` : ''}
                                    </div>
                                    {prize.description} — <span style={{ color: theme.colors.PRIMARY }}>${prize.amount}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );
};

export default StartLeagueRound; 